import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Skeleton, Switch, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import {
  Aviso,
  BORDE,
  Campo,
  estiloSwitch,
  LinkAyuda,
  PRIMARIO,
  Seccion,
  Separador,
  Subtitulo,
  TEXTO_SECUNDARIO,
  TITULO,
} from "../ui/Formulario";

const ADMIN_EVENTOS = "https://business.facebook.com/events_manager2";
const DOCS_CAPI = "https://developers.facebook.com/docs/marketing-api/conversions-api/get-started";

const Estado = ({ ok, children }) => (
  <Box
    component="span"
    sx={{
      fontSize: 12,
      fontWeight: 500,
      px: 1,
      py: 0.25,
      borderRadius: "999px",
      color: ok ? "#15803d" : TEXTO_SECUNDARIO,
      bgcolor: ok ? "#dcfce7" : "#f4f4f5",
    }}
  >
    {children}
  </Box>
);

const ConfigMetaPixel = ({ eventoId }) => {
  const [config, setConfig] = useState(null);
  const [pixel, setPixel] = useState("");
  const [activo, setActivo] = useState(false);
  const [token, setToken] = useState("");
  const [codigoPrueba, setCodigoPrueba] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState(null);

  const aplicar = (datos) => {
    setConfig(datos);
    setPixel(datos.meta_pixel_id || "");
    setActivo(Boolean(datos.activo));
    setCodigoPrueba(datos.meta_test_event_code || "");
    setToken("");
  };

  useEffect(() => {
    api.seguimiento
      .config(eventoId)
      .then((r) => aplicar(r?.data || {}))
      .catch(() => setConfig({}));
  }, [eventoId]);

  const guardar = async (extra = {}) => {
    setGuardando(true);
    try {
      const r = await api.seguimiento.guardar(eventoId, {
        meta_pixel_id: pixel.trim(),
        activo,
        meta_test_event_code: codigoPrueba.trim(),
        meta_capi_token: token.trim(),
        ...extra,
      });
      if (r.status !== "success") throw new Error(r.message);
      aplicar(r.data);
      toast.success("Guardado.");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    setPrueba(null);
    try {
      const r = await api.seguimiento.probar(eventoId);
      setPrueba({ ok: r.status === "success", mensaje: r.message });
    } catch {
      setPrueba({ ok: false, mensaje: "No se pudo hacer la prueba." });
    } finally {
      setProbando(false);
    }
  };

  const cambio =
    config &&
    (pixel.trim() !== (config.meta_pixel_id || "") ||
      activo !== Boolean(config.activo) ||
      codigoPrueba.trim() !== (config.meta_test_event_code || "") ||
      token.trim() !== "");

  const envios = config?.envios;

  const botonGuardar = (
    <Button
      variant="contained"
      disableElevation
      onClick={() => guardar()}
      disabled={!cambio || guardando || (activo && !pixel.trim())}
      sx={{ bgcolor: PRIMARIO, textTransform: "none", borderRadius: "8px", px: 2.5 }}
    >
      {guardando && <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />}
      Guardar
    </Button>
  );

  return (
    <Box>
      {config === null ? (
        <Seccion>
          <Skeleton width="30%" height={32} />
          <Skeleton height={56} />
          <Skeleton width="50%" />
        </Seccion>
      ) : (
        <>
          <Seccion titulo="Pixel" sx={{ mb: 2 }}>
            <Campo
              etiqueta="ID del pixel"
              placeholder="Ej.: 123456789012345"
              value={pixel}
              onChange={(e) => {
                const nuevo = e.target.value.replace(/\D/g, "").slice(0, 20);
                // al cargar el primer ID se activa solo: es lo que se espera al conectarlo
                if (!pixel && nuevo && !config?.meta_pixel_id) setActivo(true);
                setPixel(nuevo);
              }}
              inputProps={{ inputMode: "numeric" }}
            />
            <LinkAyuda href={ADMIN_EVENTOS}>Dónde encuentro el ID del pixel</LinkAyuda>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 2.5,
                pt: 2,
                borderTop: `1px solid ${BORDE}`,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: TITULO }}>Pixel activo</Typography>
                <Typography sx={{ fontSize: 13, color: TEXTO_SECUNDARIO }}>
                  Apagalo para pausar el envío sin perder la configuración.
                </Typography>
              </Box>
              <Switch sx={estiloSwitch} checked={activo} onChange={(e) => setActivo(e.target.checked)} inputProps={{ "aria-label": "Pixel activo" }} />
            </Box>

            <Aviso sx={{ mt: 2 }}>
              Sólo se carga en las páginas de este evento y para quienes aceptan las cookies de publicidad. Si tu pixel
              limita dominios, agregá passgo.com.ar.
            </Aviso>
          </Seccion>

          <Seccion titulo="Conversions API" accion={<Estado ok={config.token_configurado}>{config.token_configurado ? "Conectada" : "Opcional"}</Estado>}>
            <Typography sx={{ fontSize: 13, color: TEXTO_SECUNDARIO, mt: -1, mb: 2.5 }}>
              Nuestro servidor también le avisa a Meta cada compra aprobada, aunque el comprador bloquee anuncios. Meta no
              la cuenta dos veces.
            </Typography>

            <Campo
              etiqueta={config.token_configurado ? "Reemplazar token de acceso" : "Token de acceso"}
              type="password"
              autoComplete="new-password"
              placeholder={config.token_configurado ? "••••••••••••" : "Pegá el token que generaste en Meta"}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              ayuda="Por seguridad no se vuelve a mostrar después de guardarlo."
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <LinkAyuda href={DOCS_CAPI}>Cómo generar el token</LinkAyuda>
              {config.token_configurado && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => guardar({ meta_capi_token: "", quitar_token: true })}
                  disabled={guardando}
                  sx={{ textTransform: "none", mt: 1.5 }}
                >
                  Quitar token
                </Button>
              )}
            </Box>

            <Separador />

            <Subtitulo detalle="Sale de «Probar eventos» en el Administrador de eventos. Mientras esté cargado, las compras van a la pantalla de prueba: borralo al terminar.">
              Probar conexión
            </Subtitulo>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
              <Campo
                etiqueta="Código de prueba"
                placeholder="Ej.: TEST12345"
                value={codigoPrueba}
                onChange={(e) => setCodigoPrueba(e.target.value.trim().toUpperCase())}
                sx={{ flex: 1, minWidth: 180 }}
              />
              <Button
                variant="outlined"
                onClick={probar}
                disabled={probando || !config.token_configurado || !config.meta_test_event_code}
                sx={{ textTransform: "none", borderRadius: "8px", color: PRIMARIO, borderColor: PRIMARIO, height: 40, whiteSpace: "nowrap" }}
              >
                {probando && <CircularProgress size={16} sx={{ mr: 1 }} />}
                Probar conexión
              </Button>
            </Box>
            {(!config.token_configurado || !config.meta_test_event_code) && (
              <Typography sx={{ fontSize: 12, color: TEXTO_SECUNDARIO, mt: 0.75 }}>
                {!config.token_configurado ? "Guardá un token para poder probar." : "Guardá el código para poder probar."}
              </Typography>
            )}
            {prueba && (
              <Alert severity={prueba.ok ? "success" : "error"} sx={{ mt: 1.5, borderRadius: "8px" }}>
                {prueba.mensaje}
              </Alert>
            )}

            {envios?.ultimo_error && (
              <Alert severity="warning" sx={{ mt: 2.5, borderRadius: "8px" }}>
                La última compra no llegó a Meta: {envios.ultimo_error}
                {envios.pendientes > 0 &&
                  ` Hay ${envios.pendientes === 1 ? "1 compra" : `${envios.pendientes} compras`} esperando para reenviarse.`}
              </Alert>
            )}
          </Seccion>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>{botonGuardar}</Box>
        </>
      )}
    </Box>
  );
};

export default ConfigMetaPixel;
